<template>
    <!-- PRODUCT-26110 Task 5 (INSTANCE_ATTRS_CLASS_STYLE): see CriterionEnumInput.vue --
         inheritAttrs: false stops Vue 3 from auto-applying class/style to root, so
         $attrs.class is merged into the existing object-class binding below instead of
         a separate :class attribute (only one :class binding is allowed per element).
         Independent of the recursive <component> below, which already forwards the
         *whole* $attrs (including class) down to each rendered property via its own
         v-bind="$attrs" -- that was already happening before this fix and is unrelated
         to this element's own root class. -->
    <div
        class="CriterionObjectInput"
        :class="[
            $attrs.class,
            {
                'CriterionObjectInput--substitutable': customPropertySubstitutable,
                'CriterionObjectInput--error': hasError,
            },
        ]"
        :style="$attrs.style"
    >
        <template v-if="!hideTitle && form.schema.title">
            <div class="CriterionObjectInput__titleContainer">
                <div class="CriterionObjectInput__title">
                    <h1 v-if="depth === 0">
                        {{ form.schema.title }}
                    </h1>
                    <h2 v-else-if="depth === 1">
                        {{ form.schema.title }}
                    </h2>
                    <h3 v-else-if="depth === 2">
                        {{ form.schema.title }}
                    </h3>
                    <h4 v-else>
                        {{ form.schema.title }}
                    </h4>
                </div>
                <div
                    v-if="deletable && !rbacDisabled"
                    class="CriterionObjectInput__deleteIcon"
                    @click="$emit('delete')"
                >
                    <slot name="deleteIcon">
                        <EidIcon
                            :icon-src="faXmark"
                            alt="delete"
                            :svg-props="{color: alertRedColor}"
                        />
                    </slot>
                </div>
            </div>
        </template>
        <component
            :is="formElementComponent"
            v-for="property in filteredProperties"
            :id="`${id}_${property.name}`"
            :key="property.form.key"
            :depth="depth + 1"
            class="CriterionObjectInput__item"
            :class="{
                'CriterionObjectInput__valueField': property.name === 'value',
                'CriterionObjectInput__insuredField': property.name === 'insuredField',
                'CriterionObjectInput__basedOnInsuredField':
                    basedOnInsuredFieldProp && basedOnInsuredFieldProp.name === property.name,
            }"
            :custom-title="getTitleIfBasedOnInsuredField(property)"
            :form="property.form"
            :hide-title="hideTitle"
            :show-error="showError"
            :required="property.required"
            :disabled="disabled || property.form.schema.readOnly"
            :custom-component-input="customComponentInput"
            :value="getPropValue(property)"
            :touched="touched"
            :strings="strings"
            :disallowed-values="disallowedValues && disallowedValues[property.name]"
            :rbac-disabled="rbacDisabled"
            :show-validation-errors="showValidationErrors"
            v-bind="$attrs"
            @input="onInput(property, $event)"
            @change="onChange(property, $event)"
        >
            <template v-if="$slots.icon" #icon>
                <slot name="icon" />
            </template>
            <template v-if="$slots.deleteIcon" #deleteIcon>
                <slot name="deleteIcon" />
            </template>
        </component>
    </div>
</template>

<script lang="ts">
    import { Component, Prop } from '@evidentid/vue-property-decorator';

    import { faXmark } from '@fortawesome/pro-light-svg-icons';

    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import { JsonFormObject, JsonFormProperty } from '@evidentid/json-schema/interfaces/JsonForm';

    import { isSchemaCustomPropertySubstitutable }
        from '@/modules/decisioning-criteria/utils/isSchemaCustomPropertySubstitutable';
    import { tailwindColors } from '@/styles/variables/tailwind-colors/tailwindColors';

    import AbstractCriterionInput from './AbstractCriterionInput';

    @Component({
        emits: [ 'change', 'delete', 'input' ],
        inheritAttrs: false,
        components: {
            EidIcon,
        },
    })
    export default class CriterionObjectInput extends AbstractCriterionInput<JsonFormObject, object> {
        @Prop()
        protected disallowedValues: any;

        private faXmark = faXmark;
        private alertRedColor = tailwindColors.eidDangerRed.DEFAULT;

        private get properties(): JsonFormProperty[] {
            return this.form.getProperties(this.value);
        }

        private get filteredProperties(): JsonFormProperty[] {
            if (this.customPropertySubstitutable) {
                return this.properties;
            }

            return this.properties.filter((property) => !property.name.includes('BasedOnInsuredField'));
        }

        private get basedOnInsuredFieldProp(): JsonFormProperty | null {
            return this.properties.find((property) => property.name.includes('BasedOnInsuredField')) || null;
        }

        private get customPropertySubstitutable(): boolean {
            return isSchemaCustomPropertySubstitutable(this.form.schema);
        }

        private getPropValue(property: JsonFormProperty) {
            let propValue = (this.value as any)?.[property.name];
            propValue = propValue == null ? undefined : propValue;
            return property.form.getValue(propValue, true);
        }

        private onInput(property: JsonFormProperty, value: any) {
            this.$emit('input', this.form.getValue({
                ...this.value,
                [property.name]: value,
            }, true));
        }

        private onChange(property: JsonFormProperty, value: any) {
            this.$emit('change', this.form.getValue({
                ...this.value,
                [property.name]: value,
            }, true));
        }

        private getTitleIfBasedOnInsuredField(property: JsonFormProperty) {
            return this.basedOnInsuredFieldProp?.name === property.name
                ? 'Set to Field'
                : '';
        }
    }
</script>
